import dayjs from "dayjs";
import { useFormatter } from "@/utils/useFormatter";
import { organizationIdFromReference } from "../../services/resolveOrganizationDisplays";
import {
  TELECONSULTORIA_AFFILIATION_CODE_TEXT,
  TELECONSULTORIA_AFFILIATION_ROLE,
} from "../constants";
import {
  cnpjFromOrganizationToken,
  isAffiliationActive,
  organizationReferenceFromToken,
} from "../helpers/affiliationReferences";
import {
  isPendingAffiliationRequest,
  pendingRequestExtension,
} from "../helpers/affiliationRequest";
import type {
  AffiliationOrganization,
  TeleconsultoriaAffiliationRow,
} from "../types";

/**
 * Monta a afiliação de teleconsultoria.
 * - `organization` = prestadora; `participatingOrganization` = receptora.
 * - Sem extension de plano nem specialty TUSS: isso é só de convênio.
 * - `isPendingRequest` (opcional): pedido da afiliada — nasce inativo e marcado
 *   como "pending" até a prestadora aprovar.
 */
export function buildTeleconsultoriaAffiliation(params: {
  provider: AffiliationOrganization;
  receiver: AffiliationOrganization;
  isPendingRequest?: boolean;
}): fhir5.OrganizationAffiliation {
  return {
    resourceType: "OrganizationAffiliation",
    active: !params.isPendingRequest,
    ...(params.isPendingRequest && { extension: [pendingRequestExtension()] }),
    period: { start: dayjs().format("YYYY-MM-DD") },
    organization: {
      reference: organizationReferenceFromToken(params.provider.token),
      display: params.provider.name,
    },
    participatingOrganization: {
      reference: organizationReferenceFromToken(params.receiver.token),
      display: params.receiver.name,
    },
    code: [
      {
        coding: [{ ...TELECONSULTORIA_AFFILIATION_ROLE }],
        text: TELECONSULTORIA_AFFILIATION_CODE_TEXT,
      },
    ],
  };
}

/** Nome da receptora: Organization incluída no bundle → display gravado → CNPJ. */
function receiverDisplayName(
  affiliation: fhir5.OrganizationAffiliation,
  receiver: fhir5.Organization | undefined,
  receiverCnpj: string,
): string {
  return (
    receiver?.name ||
    affiliation.participatingOrganization?.display ||
    `CNPJ ${useFormatter.formatCNPJ(receiverCnpj)}`
  );
}

/** Afiliação FHIR → linha da lista. Sem id ou sem receptora, a linha é descartada. */
export function toAffiliationRow(
  affiliation: fhir5.OrganizationAffiliation,
  organizationsById: Map<string, fhir5.Organization>,
): TeleconsultoriaAffiliationRow | undefined {
  const receiverToken = organizationIdFromReference(
    affiliation.participatingOrganization?.reference,
  );
  if (!affiliation.id || !receiverToken) return undefined;

  const receiverCnpj = cnpjFromOrganizationToken(receiverToken);
  return {
    id: affiliation.id,
    isActive: isAffiliationActive(affiliation),
    isPendingRequest: isPendingAffiliationRequest(affiliation),
    receiverToken,
    receiverName: receiverDisplayName(
      affiliation,
      organizationsById.get(receiverToken),
      receiverCnpj,
    ),
    receiverCnpj,
    since: affiliation.period?.start,
    affiliation,
  };
}

/** Pedidos aguardando primeiro (pedem ação); depois ativas; depois ordem alfabética. */
export function sortAffiliationRows(
  rows: TeleconsultoriaAffiliationRow[],
): TeleconsultoriaAffiliationRow[] {
  return [...rows].sort((first, second) => {
    if (first.isPendingRequest !== second.isPendingRequest) return first.isPendingRequest ? -1 : 1;
    if (first.isActive !== second.isActive) return first.isActive ? -1 : 1;
    return first.receiverName.localeCompare(second.receiverName, "pt-BR");
  });
}
